import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { FormAsyncSelectField, lt } from '@mews/framework';
import { Selected } from '@optimus-web/core';
import { useCityLedgerMultipleInvoicesContext } from '../../../context/CityLedgerMultipleInvoicesContext';
import { useInvoiceSearch } from './queries/useInvoiceSearch';
import { useGetInvoices } from './queries/useGetInvoice';
import { renderInvoiceList } from './renderInvoiceList';

interface InvoiceSelectProps {
    name: string;
}

const ITEM_HEIGHT = 48;

export const InvoiceSelect = ({ name, ...props }: InvoiceSelectProps) => {
    const { selectedInvoices, setSelectedInvoices, invoiceIds } = useCityLedgerMultipleInvoicesContext();
    const [invoicesToUpdate, setInvoicesToUpdate] = useState<string[]>(invoiceIds);

    const { invoices = [] } = useGetInvoices(invoicesToUpdate);

    const defaultOptions = useMemo(
        () =>
            renderInvoiceList({
                invoices: selectedInvoices,
            }),
        [selectedInvoices]
    );

    const invoiceSearch = useInvoiceSearch(invoiceIds);

    const handleInvoicesChange = (_event: ChangeEvent, newInvoices: Selected) => {
        const newInvoiceIds = new Set(newInvoices as string[]);
        const selectedInvoicesIds = new Set(invoiceIds);
        const prevInvoicesSet = new Set(invoicesToUpdate);

        if (newInvoiceIds.size > selectedInvoicesIds.size) {
            const invoicesToAdd = [...newInvoiceIds.difference(selectedInvoicesIds)];
            if (invoicesToAdd.length) {
                const updatedInvoices = [...prevInvoicesSet, ...invoicesToAdd.filter((id) => !prevInvoicesSet.has(id))];
                setInvoicesToUpdate(updatedInvoices);
            }
        } else {
            setSelectedInvoices((prevSelectedInvoices) =>
                prevSelectedInvoices.filter((invoice) => (newInvoices as string[]).includes(invoice.invoiceId))
            );
        }
    };

    useEffect(() => {
        if (invoicesToUpdate.length > 0 && invoices.length > 0) {
            setSelectedInvoices((prev) => {
                const existingInvoiceIds = new Set(prev.map((invoice) => invoice.invoiceId));
                const newInvoicesToAdd = invoices.filter((invoice) => !existingInvoiceIds.has(invoice.invoiceId));
                return [...prev, ...newInvoicesToAdd];
            });
        }
    }, [invoices, invoicesToUpdate, setSelectedInvoices]);

    return (
        <FormAsyncSelectField
            {...props}
            name={name}
            required
            label={lt('SelectInvoicesToLinkPaymentLabel')}
            helper={lt('SelectInvoicesToLinkPaymentHelperMessage')}
            useDataQuery={invoiceSearch}
            defaultOptions={defaultOptions}
            renderData={renderInvoiceList}
            searchPlaceholder={lt('SearchByName')}
            multiselect
            onChange={handleInvoicesChange}
            data-test-select-name="invoices-city-ledger-link-select"
            defaultValue={invoiceIds}
            preserveMultiSelectSearch
            defaultItemHeight={ITEM_HEIGHT}
        />
    );
};
